/**
 * Admin — Registration Certificate Generator.
 *
 * Wizard-style workflow (Phase 1):
 *   1. Customer (searchable dropdown)
 *   2. Waste types (multi-select chips)
 *   3. Collection & Disposal agencies (filtered by customer's district)
 *   4. Template picker
 *   5. Dates (registration + issue + valid-until)
 *   6. Generate → PDF · WhatsApp · Email · Print
 *
 * Also shows a table of previously issued certificates.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Certificate, DownloadSimple, WhatsappLogo, EnvelopeSimple, Printer,
  Trash, X, MagnifyingGlass, CheckSquare, Square, ArrowRight,
  Palette, Plus,
} from "@phosphor-icons/react";

const TEMPLATE_ACCENTS = {
  standard: "#0B2B66",
  municipality: "#7A2E18",
  cbwtf: "#1B5E20",
  corporate_esg: "#0D47A1",
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const plusOneYear = (iso) => {
  const d = new Date(iso); d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

export default function AdminRegCerts() {
  const [customers, setCustomers] = useState([]);
  const [master, setMaster] = useState({ waste_types: [], templates: [] });
  const [collectionAgencies, setCollectionAgencies] = useState([]);
  const [disposalAgencies, setDisposalAgencies] = useState([]);
  const [certs, setCerts] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [showBranding, setShowBranding] = useState(false);
  const [emailingCert, setEmailingCert] = useState(null);

  const load = () => Promise.all([
    api.get("/reg-certs").then(r => setCerts(r.data)),
    api.get("/reg-certs/master-data").then(r => setMaster(r.data)),
    api.get("/agencies?kind=collection&active_only=true").then(r => setCollectionAgencies(r.data)),
    api.get("/agencies?kind=disposal&active_only=true").then(r => setDisposalAgencies(r.data)),
    api.get("/customers?limit=1000").then(r => setCustomers(r.data)).catch(() => {}),
  ]).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const templateMap = useMemo(() => {
    const m = {}; for (const t of master.templates) m[t.id] = t; return m;
  }, [master.templates]);

  const downloadPdf = async (cert) => {
    try {
      const r = await api.get(`/reg-certs/${cert.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${cert.cert_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printPdf = async (cert) => {
    try {
      const r = await api.get(`/reg-certs/${cert.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank"); w?.focus();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shareOnWhatsApp = async (cert) => {
    const phone = (cert.customer_snapshot?.phone || "").replace(/[^0-9]/g, "");
    if (!phone) { toast.error("Customer has no phone on file"); return; }
    const base = (window.location.origin || "").replace(/\/$/, "");
    const verify = `${base}/verify/registration-cert/${cert.cert_no}`;
    const msg = [
      `Namaste ${cert.customer_snapshot?.name || ""},`,
      "",
      `Your Waste Disposal Registration Certificate ${cert.cert_no} is ready.`,
      `Valid until ${cert.valid_until}.`,
      "",
      `Verify online: ${verify}`,
      "",
      "— Rescigo Health",
    ].join("\n");
    const waNumber = phone.length === 10 ? `91${phone}` : phone;
    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    try {
      await api.post(`/reg-certs/${cert.id}/log-whatsapp`, {});
      load();
    } catch { /* ignore */ }
  };

  const markEmailed = async (cert) => {
    const email = cert.customer_snapshot?.email;
    if (!email) { toast.error("Customer has no email on file"); return; }
    setEmailingCert(cert);
  };

  const remove = async (cert) => {
    if (!window.confirm(`Delete certificate ${cert.cert_no}? Only super-admin can do this.`)) return;
    try {
      await api.delete(`/reg-certs/${cert.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Registration Certificates"
              subtitle="Waste Disposal Registration Certificate Generator"
              actions={
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowBranding(true)}
                          data-testid="btn-reg-cert-branding"
                          className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] hover:bg-[#F7F6F2] text-[#0B2B66] rounded-md text-sm font-medium">
                    <Palette size={14} weight="duotone"/>Template branding
                  </button>
                  <button onClick={() => setShowWizard(true)}
                          data-testid="btn-new-reg-cert"
                          className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
                    <Certificate size={14} weight="fill"/>New certificate
                  </button>
                </div>
              }>
      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="reg-cert-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Cert no.</th>
              <th className="px-6 py-3">Customer</th>
              <th className="px-6 py-3">Waste streams</th>
              <th className="px-6 py-3">Template</th>
              <th className="px-6 py-3">Valid until</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {certs.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-[#8A8780]">
                <Certificate size={30} weight="duotone" color="#8A8780" className="mx-auto mb-2"/>
                No certificates issued yet. Click "New certificate" to generate one.
              </td></tr>
            )}
            {certs.map((c, i) => (
              <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} data-testid={`reg-cert-row-${c.id}`}>
                <td className="px-6 py-3 font-mono text-xs" style={{ color: TEMPLATE_ACCENTS[c.template_id] || "#0B2B66" }}>
                  {c.cert_no}
                </td>
                <td className="px-6 py-3">
                  <div className="font-medium">{c.customer_snapshot?.name}</div>
                  <div className="text-[10px] text-[#8A8780]">{c.customer_snapshot?.district}, {c.customer_snapshot?.state}</div>
                </td>
                <td className="px-6 py-3 text-xs max-w-[240px]">
                  <div className="line-clamp-2">{(c.waste_types || []).join(", ")}</div>
                </td>
                <td className="px-6 py-3 text-xs capitalize">{c.template_id.replace(/_/g, " ")}</td>
                <td className="px-6 py-3 text-xs">{c.valid_until}</td>
                <td className="px-6 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button title="Download PDF" onClick={() => downloadPdf(c)} data-testid={`rc-dl-${c.id}`}
                            className="text-[#2D5A27] text-xs inline-flex items-center gap-1">
                      <DownloadSimple size={12}/>PDF
                    </button>
                    <button title="Print" onClick={() => printPdf(c)} data-testid={`rc-print-${c.id}`}
                            className="text-[#5C5A55] text-xs inline-flex items-center gap-1">
                      <Printer size={12}/>Print
                    </button>
                    <button title="Share on WhatsApp" onClick={() => shareOnWhatsApp(c)} data-testid={`rc-wa-${c.id}`}
                            className="text-[#25D366] text-xs inline-flex items-center gap-1">
                      <WhatsappLogo size={12} weight="fill"/>WhatsApp
                    </button>
                    <button title="Mark as emailed" onClick={() => markEmailed(c)} data-testid={`rc-email-${c.id}`}
                            className="text-[#0B2B66] text-xs inline-flex items-center gap-1">
                      <EnvelopeSimple size={12}/>Email
                    </button>
                    <button title="Delete" onClick={() => remove(c)} data-testid={`rc-del-${c.id}`}
                            className="text-[#C25D38] text-xs inline-flex items-center gap-1">
                      <Trash size={12}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showWizard && (
        <Wizard
          onClose={() => setShowWizard(false)}
          customers={customers}
          master={master}
          collectionAgencies={collectionAgencies}
          disposalAgencies={disposalAgencies}
          onCreated={(c) => {
            toast.success(`Certificate ${c.cert_no} generated`);
            setShowWizard(false); load();
            // Auto-download
            downloadPdf(c);
          }}
        />
      )}

      {showBranding && (
        <TemplateBrandingModal
          templates={master.templates}
          onClose={() => setShowBranding(false)}
        />
      )}

      {emailingCert && (
        <SendEmailModal
          cert={emailingCert}
          onClose={() => setEmailingCert(null)}
          onSent={() => { setEmailingCert(null); load(); }}
        />
      )}
    </AppShell>
  );
}


// ─────────────────────────────────────────────────────────────
function Wizard({ onClose, customers, master, collectionAgencies, disposalAgencies, onCreated,
                    inline = false, prefillCustomer = null }) {
  const [q, setQ] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [wasteTypes, setWasteTypes] = useState([]);
  const [collectionAgencyId, setCollectionAgencyId] = useState("");
  const [disposalAgencyId, setDisposalAgencyId] = useState("");
  const [templateId, setTemplateId] = useState("standard");
  const [regDate, setRegDate] = useState(todayIso());
  const [issueDate, setIssueDate] = useState(todayIso());
  const [validUntil, setValidUntil] = useState(plusOneYear(todayIso()));
  const [validEdited, setValidEdited] = useState(false);
  const [notes, setNotes] = useState("");
  // Iter 220g — surface a "course completed" toggle in the wizard so
  // certificates issued to customers who finished the on-boarding
  // course carry that badge.
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [busy, setBusy] = useState(false);

  // Iter 220g — when opened from the Requests tab we jump straight to
  // the selected customer and prefill any waste types they declared
  // on the public registration form.
  useEffect(() => {
    if (prefillCustomer?.id) {
      setCustomerId(prefillCustomer.id);
      if (prefillCustomer.name) setQ(prefillCustomer.name);
      const declared = prefillCustomer.primary_waste_types || prefillCustomer.waste_types || [];
      if (declared.length) setWasteTypes(declared);
    }
    // eslint-disable-next-line
  }, [prefillCustomer?.id]);

  const customer = useMemo(
    () => customers.find(c => c.id === customerId),
    [customerId, customers],
  );

  // Auto-update valid_until when registration date changes (unless user overrode)
  const onRegDate = (v) => {
    setRegDate(v);
    if (!validEdited) setValidUntil(plusOneYear(v));
  };

  const filteredCustomers = useMemo(() => {
    if (!q.trim()) return customers.slice(0, 40);
    const needle = q.toLowerCase();
    return customers.filter(c => (
      (c.name || "").toLowerCase().includes(needle) ||
      (c.phone || "").includes(needle) ||
      (c.id || "").toLowerCase().includes(needle) ||
      (c.registration_no || "").toLowerCase().includes(needle)
    )).slice(0, 40);
  }, [q, customers]);

  const filteredAgencies = (list) => {
    if (!customer) return list;
    // Prefer same-district first, then same-state, then all
    const d = (customer.district || "").toLowerCase();
    const s = (customer.state || "").toLowerCase();
    const rank = (a) => {
      if (d && (a.district || "").toLowerCase() === d) return 0;
      if (s && (a.state || "").toLowerCase() === s) return 1;
      return 2;
    };
    return [...list].sort((a, b) => rank(a) - rank(b));
  };

  const toggleWaste = (w) => {
    setWasteTypes(cur => cur.includes(w) ? cur.filter(x => x !== w) : [...cur, w]);
  };

  const submit = async () => {
    if (!customerId) { toast.error("Select a customer"); return; }
    if (wasteTypes.length === 0) { toast.error("Select at least one waste type"); return; }
    if (!collectionAgencyId) { toast.error("Pick a collection agency"); return; }
    if (!disposalAgencyId) { toast.error("Pick a disposal agency"); return; }
    setBusy(true);
    try {
      const r = await api.post("/reg-certs", {
        customer_id: customerId,
        waste_types: wasteTypes,
        collection_agency_id: collectionAgencyId,
        disposal_agency_id: disposalAgencyId,
        template_id: templateId,
        registration_date: regDate,
        issue_date: issueDate,
        valid_until: validUntil,
        // Iter 220g — carry the course-completed flag through so it can
        // be rendered on the PDF / verification page.
        course_completed: courseCompleted,
        notes: notes,
      });
      onCreated(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  // Iter 220g — reused as both a modal (AdminRegCerts) and inline
  // panel (Customer Management → Registration Certification → Generate).
  const shell = (children) => inline ? (
    <div data-testid="reg-cert-wizard-inline"
         className="bg-white rounded-md border border-[#E5E2D9]">
      <div className="p-5 border-b border-[#E5E2D9]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Certificate wizard</div>
        <div className="font-heading text-lg mt-1">New Waste Disposal Registration Certificate</div>
        {prefillCustomer && (
          <div className="mt-2 text-[11px] text-[#5C5A55]">
            Pre-filled from certificate request for
            <strong className="text-[#DC2626] ml-1">{prefillCustomer.name}</strong>
            {prefillCustomer.phone ? ` · ${prefillCustomer.phone}` : ""}
          </div>
        )}
      </div>
      <div className="p-5 space-y-7">{children}</div>
    </div>
  ) : (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
            data-testid="reg-cert-wizard"
            className="bg-white rounded-md w-full max-w-4xl my-8 max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E5E2D9] flex justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Certificate wizard</div>
            <div className="font-heading text-lg mt-1">New Waste Disposal Registration Certificate</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>
        <div className="p-6 space-y-7">{children}</div>
      </div>
    </div>
  );

  return shell(<>
          {/* Step 1 — customer */}
          <Section step={1} title="Select customer" desc="Search by name, phone, ID or registration number.">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-3.5 text-[#8A8780]"/>
              <input value={q} onChange={e => setQ(e.target.value)}
                      placeholder="Type to search…"
                      data-testid="wiz-customer-search"
                      className="w-full h-11 pl-9 pr-3 border border-[#E5E2D9] rounded-md text-sm outline-none focus:border-[#2D5A27]"/>
            </div>
            <div className="max-h-56 overflow-y-auto mt-2 border border-[#E5E2D9] rounded-md" data-testid="wiz-customer-list">
              {filteredCustomers.length === 0 && (
                <div className="p-4 text-sm text-[#8A8780] text-center">No customers matched</div>
              )}
              {filteredCustomers.map(c => (
                <button key={c.id} type="button"
                        onClick={() => setCustomerId(c.id)}
                        data-testid={`wiz-customer-${c.id}`}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-b border-[#F0EEE7] hover:bg-[#F7F6F2] transition ${
                          customerId === c.id ? "bg-[#EFF3EE]" : ""
                        }`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{c.name || "—"}</div>
                    <div className="text-[11px] text-[#8A8780]">
                      {c.phone} · {c.district || "—"}, {c.state || "—"}
                    </div>
                  </div>
                  {customerId === c.id && <CheckSquare size={16} weight="fill" color="#2D5A27"/>}
                </button>
              ))}
            </div>
          </Section>

          {/* Step 2 — waste types */}
          <Section step={2} title="Waste categories" desc="Pick one or more streams. All 32 CPCB categories supported.">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="wiz-waste-grid">
              {master.waste_types.map(w => {
                const on = wasteTypes.includes(w);
                return (
                  <button key={w} type="button" onClick={() => toggleWaste(w)}
                          data-testid={`wiz-waste-${w.replace(/\s+/g, "-").toLowerCase()}`}
                          className={`text-left px-3 py-2 rounded-md text-xs border transition ${
                            on
                              ? "border-[#2D5A27] bg-[#EFF3EE] text-[#1A3317] font-medium"
                              : "border-[#E5E2D9] bg-white text-[#5C5A55] hover:bg-[#F7F6F2]"
                          }`}>
                    <span className="inline-flex items-center gap-1.5">
                      {on ? <CheckSquare size={12} weight="fill"/> : <Square size={12}/>}
                      {w}
                    </span>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Step 3 — agencies */}
          <Section step={3} title="Collection & Disposal agencies"
                    desc={customer ? `Sorted by proximity to ${customer.district || "the customer's region"}.` : "Pick the collector and the CBWTF."}>
            <div className="grid md:grid-cols-2 gap-4">
              <AgencySelect label="Collection agency"
                             list={filteredAgencies(collectionAgencies)}
                             value={collectionAgencyId} onChange={setCollectionAgencyId}
                             testid="wiz-ca"/>
              <AgencySelect label="Disposal facility (CBWTF)"
                             list={filteredAgencies(disposalAgencies)}
                             value={disposalAgencyId} onChange={setDisposalAgencyId}
                             testid="wiz-da"/>
            </div>
            <div className="text-[11px] text-[#8A8780] mt-2">
              Missing an agency? Add it in <a href="/admin/agencies" target="_blank" rel="noreferrer" className="underline text-[#2D5A27]">Agency Directory</a>.
            </div>
          </Section>

          {/* Step 4 — template */}
          <Section step={4} title="Template" desc="Pick the visual template. Phase 2 will let you upload custom templates.">
            <div className="grid md:grid-cols-2 gap-2" data-testid="wiz-templates">
              {master.templates.map(t => (
                <button key={t.id} type="button" onClick={() => setTemplateId(t.id)}
                        data-testid={`wiz-tpl-${t.id}`}
                        className={`text-left p-4 rounded-md border transition ${
                          templateId === t.id ? "border-2 border-[#2D5A27] bg-[#EFF3EE]"
                                              : "border-[#E5E2D9] bg-white hover:bg-slate-50"
                        }`}>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: TEMPLATE_ACCENTS[t.id] }}>
                    {t.id.replace(/_/g, " ")}
                  </div>
                  <div className="font-heading text-sm mt-1">{t.title}</div>
                  <div className="text-[11px] text-[#8A8780] mt-1">{t.subtitle}</div>
                </button>
              ))}
            </div>
          </Section>

          {/* Step 5 — dates */}
          <Section step={5} title="Registration validity" desc="Valid Until auto-fills as Registration Date + 1 year.">
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Registration date" type="date" value={regDate}
                      onChange={onRegDate} testid="wiz-reg-date"/>
              <Field label="Issue date" type="date" value={issueDate}
                      onChange={setIssueDate} testid="wiz-issue-date"/>
              <Field label="Valid until" type="date" value={validUntil}
                      onChange={(v) => { setValidUntil(v); setValidEdited(true); }}
                      testid="wiz-valid-until"/>
            </div>
            <label className="block mt-4">
              <span className="block text-xs text-[#5C5A55] mb-1">Notes (optional, internal)</span>
              <textarea rows={2} value={notes}
                         onChange={e => setNotes(e.target.value)}
                         data-testid="wiz-notes"
                         className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#2D5A27]"/>
            </label>
            {/* Iter 220g — course completed toggle */}
            <label className="mt-4 flex items-center gap-2 text-sm text-[#1A1A1A] cursor-pointer">
              <input type="checkbox" checked={courseCompleted}
                     onChange={(e) => setCourseCompleted(e.target.checked)}
                     data-testid="wiz-course-completed"
                     className="w-4 h-4 rounded border-[#E5E2D9] text-[#2D5A27] focus:ring-[#2D5A27]"/>
              <span>
                Customer has <strong>completed the Rescigo waste-handler course</strong>
                <span className="text-[11px] text-[#8A8780] ml-2">(shown on the certificate)</span>
              </span>
            </label>
          </Section>

          <div className="rounded-md bg-[#EFF3EE] border border-[#D4E0D1] p-4 text-xs text-[#1A3317] flex items-start gap-2">
            <Certificate size={14} weight="fill" color="#2D5A27"/>
            <div>
              PDF is generated with an auto-numbered <b>RH-WRC-YYYY-XXXXXX</b> reference and a QR that verifies against
              <span className="font-mono"> /verify/registration-cert/&lt;cert_no&gt;</span>.
              You'll get a WhatsApp / Email / Download button on the next screen.
            </div>
          </div>

        <div className="p-6 border-t border-[#E5E2D9] flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose}
                  className={`h-10 px-4 border border-[#E5E2D9] rounded-md text-sm ${inline ? "hidden" : ""}`}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy}
                  data-testid="wiz-submit"
                  className="inline-flex items-center gap-2 h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium disabled:opacity-50">
            {busy ? "Generating…" : "Generate certificate"}
            <ArrowRight size={13} weight="bold"/>
          </button>
        </div>
      </>);
}


function Section({ step, title, desc, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-7 h-7 rounded-full bg-[#EFF3EE] text-[#2D5A27] flex items-center justify-center text-xs font-semibold">
          {step}
        </div>
        <div>
          <div className="text-sm font-medium text-slate-900">{title}</div>
          <div className="text-[11px] text-[#8A8780]">{desc}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

function AgencySelect({ label, list, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#5C5A55] mb-1">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)}
               data-testid={testid}
               className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#2D5A27]">
        <option value="">— select —</option>
        {list.map(a => (
          <option key={a.id} value={a.id}>
            {a.name} — {a.district}, {a.state}
          </option>
        ))}
      </select>
    </label>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#5C5A55] mb-1">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
              data-testid={testid}
              className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#2D5A27]"/>
    </label>
  );
}


// ─────────────────────────────────────────────────────────────
// Phase 2 — Template Branding (multi-logo, seals, watermark, signatures)
// ─────────────────────────────────────────────────────────────
const POSITIONS = [
  "top-left", "top-center", "top-right",
  "middle-left", "middle-center", "middle-right",
  "bottom-left", "bottom-center", "bottom-right",
];
const SIZES = ["small", "medium", "large"];
const EMPTY_WATERMARK = { url: "", opacity: 0.08, position: "middle-center" };
const emptyLogo = () => ({ name: "", logo_url: "", position: "top-center", size: "medium", order: 0, enabled: true });
const emptySig = () => ({ name: "", designation: "", department: "", signature_url: "", position: "bottom-right", size: "medium", order: 0, enabled: true });

const PLACEHOLDERS = [
  "customer_name", "certificate_no", "registration_date", "issue_date",
  "valid_until", "waste_types", "collection_agency", "disposal_agency",
  "municipality", "ward", "district", "state", "phone",
];

function TemplateBrandingModal({ templates, onClose, inline = false }) {
  const [templateId, setTemplateId] = useState(templates?.[0]?.id || "standard");
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!templateId) return;
    setLoading(true);
    api.get(`/reg-certs/template-config/${templateId}`)
      .then(r => setCfg({
        template_id: templateId,
        main_logo_url: r.data.main_logo_url || "",
        main_logo_position: r.data.main_logo_position || "top-left",
        main_logo_size: r.data.main_logo_size || "medium",
        partner_logos: r.data.partner_logos || [],
        signatories: r.data.signatories || [],
        seals: r.data.seals || [],
        watermark: r.data.watermark || { ...EMPTY_WATERMARK },
        border_image: r.data.border_image || { url: "", opacity: 0.6 },
        custom_title: r.data.custom_title || "",
        custom_subtitle: r.data.custom_subtitle || "",
        body_text: r.data.body_text || "",
        footer_text: r.data.footer_text || "",
      }))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [templateId]);

  const patch = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/reg-certs/template-config", cfg);
      toast.success("Template branding saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  // Iter 220g — reused inline in Customer Management → Registration
  // Certification → Settings tab.
  const shell = (children) => inline ? (
    <div data-testid="template-branding-inline"
         className="bg-white rounded-md border border-[#E5E2D9]">
      <div className="p-5 border-b border-[#E5E2D9]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Template branding</div>
        <div className="font-heading text-lg mt-1">Customise certificate template</div>
      </div>
      <div className="p-5 space-y-6">{children}</div>
    </div>
  ) : (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           data-testid="template-branding-modal"
           className="bg-white rounded-md w-full max-w-4xl my-8 max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E5E2D9] flex justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Phase 2 · Template branding</div>
            <div className="font-heading text-lg mt-1">Customise certificate template</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>
        <div className="p-6 space-y-6">{children}</div>
      </div>
    </div>
  );

  return shell(<>
          <div className="flex flex-wrap gap-2" data-testid="branding-template-tabs">
            {templates.map(t => (
              <button key={t.id} onClick={() => setTemplateId(t.id)}
                      data-testid={`branding-tab-${t.id}`}
                      className={`px-3 h-9 rounded-md text-xs border ${
                        templateId === t.id
                          ? "border-[#2D5A27] bg-[#EFF3EE] text-[#1A3317] font-medium"
                          : "border-[#E5E2D9] bg-white text-[#5C5A55] hover:bg-[#F7F6F2]"
                      }`}>
                {t.title || t.id}
              </button>
            ))}
          </div>

          {loading && <div className="p-8 text-center text-sm text-[#8A8780]">Loading…</div>}

          {!loading && cfg && (
            <div className="space-y-8">
              {/* Custom text */}
              <BSection title="Header text" desc="Override the printed title, subtitle and footer. Leave blank to keep the template default.">
                <div className="grid md:grid-cols-2 gap-3">
                  <TextInput label="Custom title" value={cfg.custom_title}
                             onChange={v => patch("custom_title", v)} testid="branding-custom-title"/>
                  <TextInput label="Custom subtitle" value={cfg.custom_subtitle}
                             onChange={v => patch("custom_subtitle", v)} testid="branding-custom-subtitle"/>
                </div>
                <TextInput label="Footer text" value={cfg.footer_text}
                           onChange={v => patch("footer_text", v)} testid="branding-footer"/>
              </BSection>

              {/* Body text */}
              <BSection title="Certificate body (with placeholders)"
                        desc={`Use tokens like {{customer_name}} — they'll be replaced when the PDF renders. Supported: ${PLACEHOLDERS.map(p => "{{" + p + "}}").join(", ")}`}>
                <textarea rows={5} value={cfg.body_text}
                          onChange={e => patch("body_text", e.target.value)}
                          data-testid="branding-body-text"
                          placeholder="Leave blank to use the built-in registration statement."
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#2D5A27] font-mono"/>
              </BSection>

              {/* Main logo */}
              <BSection title="Main logo" desc="Displayed prominently on the certificate. Paste any HTTPS URL — the Media Library is a good source.">
                <div className="grid md:grid-cols-3 gap-3">
                  <TextInput label="Logo URL" value={cfg.main_logo_url}
                             onChange={v => patch("main_logo_url", v)} testid="branding-main-logo-url"/>
                  <SelectInput label="Position" value={cfg.main_logo_position}
                               onChange={v => patch("main_logo_position", v)}
                               options={POSITIONS} testid="branding-main-logo-pos"/>
                  <SelectInput label="Size" value={cfg.main_logo_size}
                               onChange={v => patch("main_logo_size", v)}
                               options={SIZES} testid="branding-main-logo-size"/>
                </div>
                {cfg.main_logo_url && (
                  <div className="mt-2"><img src={cfg.main_logo_url} alt="logo"
                        className="h-16 object-contain border border-[#E5E2D9] rounded bg-white p-1"
                        onError={e => { e.target.style.display = "none"; }}/></div>
                )}
              </BSection>

              {/* Partner logos */}
              <RepeatingList
                title="Partner logos"
                items={cfg.partner_logos}
                onChange={arr => patch("partner_logos", arr)}
                empty={emptyLogo}
                render={(it, upd, i) => (
                  <LogoRow it={it} upd={upd} idx={i} testid="branding-partner"/>
                )}
                addLabel="Add partner logo"
                testid="branding-partner-logos"
              />

              {/* Seals */}
              <RepeatingList
                title="Seals & stamps"
                items={cfg.seals}
                onChange={arr => patch("seals", arr)}
                empty={emptyLogo}
                render={(it, upd, i) => (
                  <LogoRow it={it} upd={upd} idx={i} testid="branding-seal"/>
                )}
                addLabel="Add seal"
                testid="branding-seals"
              />

              {/* Signatories */}
              <RepeatingList
                title="Signatories"
                items={cfg.signatories}
                onChange={arr => patch("signatories", arr)}
                empty={emptySig}
                render={(it, upd, i) => (
                  <SignatoryRow it={it} upd={upd} idx={i}/>
                )}
                addLabel="Add signatory"
                testid="branding-signatories"
              />

              {/* Watermark */}
              <BSection title="Watermark" desc="A subtle background logo. Opacity range 0–25%.">
                <div className="grid md:grid-cols-3 gap-3">
                  <TextInput label="Watermark URL" value={cfg.watermark?.url || ""}
                             onChange={v => patch("watermark", { ...cfg.watermark, url: v })}
                             testid="branding-wm-url"/>
                  <SelectInput label="Position" value={cfg.watermark?.position || "middle-center"}
                               onChange={v => patch("watermark", { ...cfg.watermark, position: v })}
                               options={["middle-center", "tile"]} testid="branding-wm-pos"/>
                  <div>
                    <span className="block text-xs text-[#5C5A55] mb-1">
                      Opacity: {Math.round((cfg.watermark?.opacity || 0) * 100)}%
                    </span>
                    <input type="range" min="0" max="0.25" step="0.01"
                           value={cfg.watermark?.opacity || 0.08}
                           onChange={e => patch("watermark", { ...cfg.watermark, opacity: parseFloat(e.target.value) })}
                           data-testid="branding-wm-opacity"
                           className="w-full accent-[#2D5A27]"/>
                  </div>
                </div>
              </BSection>

              {/* Border image */}
              <BSection title="Certificate border image"
                        desc="Full-A4 decorative frame. Rendered behind all text and images. Opacity 0–100%. When set, the default coloured border is hidden.">
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <TextInput label="Border image URL (A4 portrait, transparent PNG recommended)"
                               value={cfg.border_image?.url || ""}
                               onChange={v => patch("border_image", { ...cfg.border_image, url: v })}
                               testid="branding-border-url"/>
                  </div>
                  <div>
                    <span className="block text-xs text-[#5C5A55] mb-1">
                      Opacity: {Math.round((cfg.border_image?.opacity ?? 0.6) * 100)}%
                    </span>
                    <input type="range" min="0" max="1" step="0.01"
                           value={cfg.border_image?.opacity ?? 0.6}
                           onChange={e => patch("border_image", { ...cfg.border_image, opacity: parseFloat(e.target.value) })}
                           data-testid="branding-border-opacity"
                           className="w-full accent-[#2D5A27]"/>
                  </div>
                </div>
                {cfg.border_image?.url && (
                  <div className="mt-2">
                    <img src={cfg.border_image.url} alt="border preview"
                          className="max-h-40 object-contain border border-[#E5E2D9] rounded bg-white p-1"
                          onError={e => { e.target.style.display = "none"; }}/>
                  </div>
                )}
              </BSection>
            </div>
          )}

        <div className="p-6 border-t border-[#E5E2D9] flex justify-between items-center sticky bottom-0 bg-white">
          <div className="text-[11px] text-[#8A8780]">
            Tip: Upload assets to <a href="/admin/media" target="_blank" rel="noreferrer" className="underline text-[#2D5A27]">Media Library</a> and copy the URLs here.
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
                    className={`h-10 px-4 border border-[#E5E2D9] rounded-md text-sm ${inline ? "hidden" : ""}`}>Cancel</button>
            <button type="button" onClick={save} disabled={busy || !cfg}
                    data-testid="branding-save"
                    className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
              {busy ? "Saving…" : "Save branding"}
            </button>
          </div>
        </div>
      </>);
}

function BSection({ title, desc, children }) {
  return (
    <div>
      <div className="mb-2">
        <div className="text-sm font-medium text-slate-900">{title}</div>
        {desc && <div className="text-[11px] text-[#8A8780] mt-0.5">{desc}</div>}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function RepeatingList({ title, items, onChange, empty, render, addLabel, testid }) {
  const add = () => onChange([...items, { ...empty(), order: items.length }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, upd) => onChange(items.map((it, idx) => idx === i ? { ...it, ...upd } : it));
  return (
    <BSection title={title}>
      <div className="space-y-2" data-testid={testid}>
        {items.length === 0 && (
          <div className="p-4 text-center text-xs text-[#8A8780] border border-dashed border-[#E5E2D9] rounded-md">
            None configured
          </div>
        )}
        {items.map((it, i) => (
          <div key={i} className="p-3 border border-[#E5E2D9] rounded-md bg-[#FAFAF7]">
            <div className="flex justify-between items-center mb-2">
              <div className="text-[11px] text-[#5C5A55]">#{i + 1}</div>
              <button onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}
                      className="text-[#C25D38] hover:bg-[#FDE9E4] p-1 rounded">
                <Trash size={12}/>
              </button>
            </div>
            {render(it, (upd) => update(i, upd), i)}
          </div>
        ))}
      </div>
      <button onClick={add} data-testid={`${testid}-add`}
              className="inline-flex items-center gap-1 h-9 px-3 border border-dashed border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFF3EE] rounded-md text-xs">
        <Plus size={12}/>{addLabel}
      </button>
    </BSection>
  );
}

function LogoRow({ it, upd, idx, testid }) {
  return (
    <div className="grid md:grid-cols-5 gap-2 items-start">
      <TextInput label="Name" value={it.name} onChange={v => upd({ name: v })} testid={`${testid}-name-${idx}`}/>
      <div className="md:col-span-2">
        <TextInput label="Image URL" value={it.logo_url} onChange={v => upd({ logo_url: v })} testid={`${testid}-url-${idx}`}/>
      </div>
      <SelectInput label="Position" value={it.position} onChange={v => upd({ position: v })} options={POSITIONS} testid={`${testid}-pos-${idx}`}/>
      <SelectInput label="Size" value={it.size} onChange={v => upd({ size: v })} options={SIZES} testid={`${testid}-size-${idx}`}/>
      {it.logo_url && (
        <div className="md:col-span-5">
          <img src={it.logo_url} alt="" className="h-12 object-contain border border-[#E5E2D9] rounded bg-white p-1"
               onError={e => { e.target.style.display = "none"; }}/>
        </div>
      )}
    </div>
  );
}

function SignatoryRow({ it, upd, idx }) {
  return (
    <div className="space-y-2">
      <div className="grid md:grid-cols-3 gap-2">
        <TextInput label="Name" value={it.name} onChange={v => upd({ name: v })} testid={`sig-name-${idx}`}/>
        <TextInput label="Designation" value={it.designation} onChange={v => upd({ designation: v })} testid={`sig-desig-${idx}`}/>
        <TextInput label="Department" value={it.department} onChange={v => upd({ department: v })} testid={`sig-dept-${idx}`}/>
      </div>
      <div className="grid md:grid-cols-3 gap-2">
        <div className="md:col-span-2">
          <TextInput label="Signature image URL" value={it.signature_url} onChange={v => upd({ signature_url: v })} testid={`sig-url-${idx}`}/>
        </div>
        <SelectInput label="Position" value={it.position} onChange={v => upd({ position: v })} options={POSITIONS} testid={`sig-pos-${idx}`}/>
      </div>
    </div>
  );
}

function TextInput({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-[#5C5A55] mb-1">{label}</span>
      <input value={value || ""} onChange={e => onChange(e.target.value)}
             placeholder={placeholder}
             data-testid={testid}
             className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs outline-none focus:border-[#2D5A27]"/>
    </label>
  );
}

function SelectInput({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-[#5C5A55] mb-1">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)}
              data-testid={testid}
              className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs outline-none focus:border-[#2D5A27]">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}


// ─────────────────────────────────────────────────────────────
// Phase 2 — Real Resend email dispatch
// ─────────────────────────────────────────────────────────────
function SendEmailModal({ cert, onClose, onSent }) {
  const [toEmail, setToEmail] = useState(cert.customer_snapshot?.email || "");
  const [subject, setSubject] = useState(
    `Your Waste Registration Certificate ${cert.cert_no}`,
  );
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!toEmail.trim()) { toast.error("Enter recipient email"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/reg-certs/${cert.id}/send-email`, {
        to_email: toEmail.trim(),
        subject: subject.trim() || undefined,
        body: body.trim() || undefined,
      });
      toast.success(`Certificate emailed to ${r.data.to}`);
      onSent();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           data-testid="reg-cert-email-modal"
           className="bg-white rounded-md w-full max-w-lg">
        <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Email certificate</div>
            <div className="font-heading text-lg mt-1">{cert.cert_no}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs text-[#5C5A55] mb-1">Recipient</label>
            <input value={toEmail} onChange={e => setToEmail(e.target.value)}
                   data-testid="reg-cert-email-to"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
          <div>
            <label className="block text-xs text-[#5C5A55] mb-1">Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)}
                   data-testid="reg-cert-email-subject"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
          <div>
            <label className="block text-xs text-[#5C5A55] mb-1">Message (HTML — leave blank for default)</label>
            <textarea rows={5} value={body} onChange={e => setBody(e.target.value)}
                      data-testid="reg-cert-email-body"
                      placeholder="Leave blank to use the default certificate email."
                      className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
          <div className="text-[11px] text-[#8A8780]">
            The generated PDF will be attached automatically.
          </div>
        </div>
        <div className="p-6 border-t border-[#E5E2D9] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={send} disabled={busy}
                  data-testid="reg-cert-email-send"
                  className="inline-flex items-center gap-2 h-10 px-5 bg-[#0B2B66] hover:bg-[#0f3579] text-white rounded-md text-sm font-medium disabled:opacity-50">
            <EnvelopeSimple size={13}/>{busy ? "Sending…" : "Send now"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Iter 220g — re-export internals so the new customer-management
// "Registration Certification" sub-module can reuse them without
// duplicating the 900 lines of wizard / branding / email logic.
export { Wizard, TemplateBrandingModal, SendEmailModal, TEMPLATE_ACCENTS };
